
import React,{useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom/client";
import Cookies from "js-cookie";
import {
  Route,
  Routes,
  useNavigate,
  BrowserRouter
} from "react-router-dom";
import {Provider, useDispatch} from 'react-redux'
import { ToastContainer } from 'react-toastify';

import "react-datepicker/dist/react-datepicker.css";
import "assets/plugins/nucleo/css/nucleo.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "assets/css/argon-dashboard-react.scss";
import "assets/css/general.css";
import 'react-toastify/dist/ReactToastify.css';

import packageJson from '../package.json';
import PatientLayout from "layouts/Patient.js";
import AuthLayout from "layouts/Auth.js";
import DoctorLayout from "layouts/Doctor"
import AdminLayout from "layouts/Admin";
import { store } from "store";
import {
  fetchUserData,
  ensureTabPresence,
  startHeartbeat,
  removeCurrentTab,
  markPendingLastTabLogout,
  resolvePendingLastTabLogout,
  clearTabSessionState
} from "./index-util"

const MINUTE =  60 * 1000 
const SIGNOUT_TIME = {
  doctor:  60 * MINUTE,
  patient: 15 * MINUTE,
  admin:   15 * MINUTE
}
const WARNING_TIME = 15000

export default function App() {
  const [user, setUser] = useState()
  const isAutoLogout = useRef(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  
  let warnTimeout;
  let logoutTimeout;
  const tabId = sessionStorage.getItem('portal_tab_id') || `${Date.now()}-${Math.random()}`
  sessionStorage.setItem('portal_tab_id', tabId)

  const LOGOUT_BROADCAST_KEY = 'portal_logout_broadcast'

  console.log(`CardiacSense Portal ${packageJson.version}`)
  
  useEffect( () => {
    const { shouldLogout: shouldLogoutFromPending, wasReload } = resolvePendingLastTabLogout(tabId)
    if (process.env.NODE_ENV !== 'production') {
      console.log('[portal session] startup', {
        tabId,
        wasReload,
        shouldLogoutFromPending,
        hasJwtToken: !!localStorage.getItem('jwtToken')
      })
    }
    if (shouldLogoutFromPending) {
      Cookies.remove('user')
      localStorage.removeItem('jwtToken')
      setUser(null)
    }

    const hadOtherTabs = ensureTabPresence(tabId)
    if (process.env.NODE_ENV !== 'production') {
      console.log('[portal session] tab presence', {
        tabId,
        hadOtherTabs,
        wasReload
      })
    }
    // If there are no other active tabs and this wasn't a reload, force fresh login.
    // This also handles cases where the tab closed/crashed and unload never ran.
    if (!hadOtherTabs && !wasReload) {
      Cookies.remove('user')
      localStorage.removeItem('jwtToken')
      setUser(null)
    }

    const hb = startHeartbeat(tabId)

    const onStorage = (e) => {
      if (e?.key === LOGOUT_BROADCAST_KEY && e.newValue) {
        logout({ broadcast: false })
      }
    }
    window.addEventListener('storage', onStorage)

    fetchUserData(dispatch, logout, setUser)

    return () => {
      clearInterval(hb)
      const noTabsRemain = removeCurrentTab(tabId)
      if (noTabsRemain) {
        // Don't delete the token here (would also run on refresh).
        // Instead, mark a pending logout; next app load decides if it was a reload or a real close.
        markPendingLastTabLogout(tabId)
      }

      window.removeEventListener('storage', onStorage)
    }
  },[])

  useEffect(() => {
    if(!user?.type) return
    if(isAutoLogout.current) return
    isAutoLogout.current = true // for blocking duplicate listeners
    initAutoLogout()
  }, [user])
    
  const logout = ({ broadcast = true } = {}) => {
    console.log('LOGOUT TRIGGERED');
    localStorage.setItem('last_session_event', new Date())
    Cookies.remove('user') ;
    localStorage.removeItem('jwtToken')
    clearTabSessionState()
    if (broadcast) {
      try {
        localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()))
      } catch (e) {
        // ignore
      }
    }
    setUser(null)
    navigate('/login')
  }

  const getLayout = (props) => {
    const currentJwToken = localStorage.getItem('jwtToken');
    if (!user) {
      if (currentJwToken) return <div></div>;
      return <AuthLayout {...props} setUser={setUser} />
    }

    switch (user.type) {
      case 'patient':
        return <PatientLayout {...props} user={user} setUser={setUser} logout={logout} />;
      case 'doctor':
        return <DoctorLayout {...props} user={user} setUser={setUser} logout={logout} />;
      case 'admin':
        return <AdminLayout {...props} user={user} setUser={setUser} logout={logout} />;
      default:{
        return <AuthLayout {...props} setUser={setUser} />
      }
    }
  };

  const setTimeouts = () => {
    warnTimeout = setTimeout(
      () => console.log('Warning'),
      WARNING_TIME
    )
    logoutTimeout = setTimeout(logout, SIGNOUT_TIME[user.type])
  };

  const clearTimeouts = () => {
    if (warnTimeout) clearTimeout(warnTimeout);
    if (logoutTimeout) clearTimeout(logoutTimeout);
  };

  const resetTimeout = () => {
    clearTimeouts();
    setTimeouts();
  };

  const initAutoLogout = () => {
    setTimeouts();
    const events = ['load','mousemove','mousedown','click','scroll','keypress'];
    for (let i = 0; i < events.length; i += 1) {
      window.addEventListener(events[i], resetTimeout);
    }
  };

  return (
    <>
      <ToastContainer />
      <Routes>
        <Route path="*" element={getLayout()} />
      </Routes>
    </>
  )
}

const root = ReactDOM.createRoot(
 document.getElementById('root')
);
root.render(
    <Provider store = {store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>);

